/**
 * Register Closure Missing in Xero Config (accounting domain)
 *
 * Covers ``ex-register-closure-missing-in-xero``. Used to live under
 * ``system/registerExceptions.ts``; moved here because the concern is
 * the Xero ACCREC / ACCPAY journal-posting integrity, not register
 * operations.
 *
 * Reuses ``renderOutlet``, ``renderRegister``, and ``platformCommonColumns``
 * from the still-system-domain register configs to avoid duplication.
 */
import React from 'react'
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { withTooltip } from '../shared/muiElements'
import {
  Warning as WarningIcon,
  History as HistoryIcon,
} from '@mui/icons-material'
import { handleExternalLinkClick } from '../../../utils/externalLinks'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { composeRowSubtitle, datePart, textPart } from '../shared/rowSubtitle'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'
import { formatBusinessDate } from '../../../utils/inspectorFormatters'
import {
  renderOutlet,
  renderRegister,
  platformCommonColumns,
} from '../system/registerExceptions'

const renderClosureSequenceCell = (
  seqKey: 'prev' | 'next'
) => (value: any, row: Record<string, any>): React.ReactNode => {
  const meta = row.exception_metadata || {}
  const seq = meta[`${seqKey}_sequence`]
  const date = meta[`${seqKey}_date`]
  const inv = meta[`${seqKey}_invoice_number`]
  const link = meta[`${seqKey}_xero_link`]
  if (seq === undefined || seq === null) return '-'

  const body = React.createElement(
    Box,
    { sx: { display: 'flex', flexDirection: 'column', lineHeight: 1.2 } },
    React.createElement(
      Typography,
      {
        variant: 'body2',
        fontWeight: 'medium',
        sx: link ? { color: 'primary.main', textDecoration: 'underline' } : undefined,
      },
      `#${seq}`
    ),
    date &&
      React.createElement(
        Typography,
        { variant: 'caption', color: 'text.secondary' },
        formatBusinessDate(date)
      )
  )

  const wrapped = link
    ? React.createElement(
        // Box renders as an <a>; createElement infers the div variant, so
        // href/onClick (anchor-typed) need the component cast to type-check.
        Box as any,
        {
          component: 'a',
          href: link,
          target: '_blank',
          rel: 'noopener noreferrer',
          onClick: (e: React.MouseEvent<HTMLAnchorElement>) =>
            handleExternalLinkClick(e as any, link, `View ${seqKey} closure in Xero`),
          sx: { textDecoration: 'none', color: 'inherit', display: 'inline-block' },
        },
        body
      )
    : body

  return withTooltip(
    { title: inv ? `Invoice ${inv}${link ? ' · click to open in Xero' : ''}` : '' },
    wrapped
  )
}

const renderMissingSequence = (value: any, row: Record<string, any>): React.ReactNode => {
  const meta = row.exception_metadata || {}

  // Tail rows: LS register_close_time has no matching Xero ACCREC within
  // ±tolerance. Sequence is unknown by definition - render the "recent
  // close not in Xero" indicator plus the LS close date + hours-since.
  if (meta.detection_type === 'tail') {
    const lsCloseTime = meta.ls_register_close_time as string | undefined
    const lastXeroSyncAt = meta.last_xero_sync_at as string | undefined
    const xeroCoverageAnchor = meta.xero_coverage_anchor as string | undefined
    const hoursSince = meta.hours_since_last_xero_accrec as number | null | undefined
    const tooltipText = lsCloseTime
      ? `LS register last closed ${formatBusinessDate(lsCloseTime)} - no matching Xero ACCREC closure within ±${meta.tail_tolerance_hours ?? 5} h`
      : 'No matching Xero ACCREC closure for the latest LS register close'
    return withTooltip(
      { title: tooltipText },
      React.createElement(
        Box,
        {
          sx: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.25,
            lineHeight: 1.2,
          },
        },
        React.createElement(Chip, {
          label: 'Recent close not in Xero',
          color: 'warning',
          size: 'small',
          icon: React.createElement(WarningIcon, { fontSize: 'small' }),
        }),
        lsCloseTime &&
          React.createElement(
            Typography,
            { variant: 'caption', color: 'text.secondary' },
            `LS closed: ${formatBusinessDate(lsCloseTime)}`
          ),
        lastXeroSyncAt &&
          React.createElement(
            Typography,
            { variant: 'caption', color: 'text.secondary' },
            `Last Xero sync: ${formatBusinessDate(lastXeroSyncAt)}`
          ),
        xeroCoverageAnchor &&
          React.createElement(
            Typography,
            { variant: 'caption', color: 'text.secondary' },
            `Xero data through: ${formatBusinessDate(xeroCoverageAnchor)}`
          ),
        typeof hoursSince === 'number' &&
          React.createElement(
            Typography,
            {
              variant: 'caption',
              color: 'text.secondary',
              sx: { fontStyle: 'italic', fontSize: '0.7rem' },
            },
            `${hoursSince}h since last Xero ACCREC`
          )
      )
    )
  }

  const seq = meta.missing_sequence
  if (seq === undefined || seq === null) return '-'
  const billPresent = !!meta.bill_present
  return React.createElement(
    Box,
    {
      sx: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.25,
        lineHeight: 1.2,
      },
    },
    React.createElement(Chip, {
      label: `#${seq}`,
      color: 'error',
      size: 'small',
    }),
    billPresent &&
      withTooltip(
        { title: 'COGS bill arrived in Xero but the sales invoice did not' },
        React.createElement(
          Typography,
          {
            variant: 'caption',
            color: 'text.secondary',
            sx: { fontStyle: 'italic', fontSize: '0.7rem' },
          },
          'COGS bill present'
        )
      )
  )
}

const renderClosureHistoryAction = (value: any, row: Record<string, any>): React.ReactNode => {
  if (!row.lightspeed_register_closure_link) return null
  return React.createElement(
    Box,
    { sx: { display: 'flex', gap: 0.5 } },
    withTooltip(
      { title: 'View Closure History' },
      React.createElement(
        IconButton,
        {
          size: 'small',
          onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault()
            handleExternalLinkClick(
              e as any,
              row.lightspeed_register_closure_link,
              'View Closure History'
            )
          },
          sx: { color: 'primary.main' },
        },
        React.createElement(HistoryIcon, { fontSize: 'medium' })
      )
    )
  )
}

const missingClosureInspectorColumns: ExceptionColumnDef[] = [
  {
    key: 'outlet_name',
    label: 'Outlet',
    contexts: ['inspector'],
    render: renderOutlet,
  },
  {
    key: 'register_name',
    label: 'Register',
    contexts: ['inspector'],
    render: renderRegister,
  },
  {
    key: 'missing_sequence',
    label: 'Missing Closure',
    align: 'center',
    contexts: ['inspector'],
    render: renderMissingSequence,
    getSortValue: (row) => row.exception_metadata?.missing_sequence ?? -1,
  },
  {
    key: 'prev_sequence',
    label: 'Previous',
    contexts: ['inspector'],
    render: renderClosureSequenceCell('prev'),
    getSortValue: (row) => row.exception_metadata?.prev_date ?? null,
  },
  {
    key: 'next_sequence',
    label: 'Next',
    contexts: ['inspector'],
    render: renderClosureSequenceCell('next'),
    getSortValue: (row) => row.exception_metadata?.next_date ?? null,
  },
  {
    key: 'actions',
    label: 'Actions',
    align: 'center',
    contexts: ['inspector'],
    render: renderClosureHistoryAction,
  },
]

const missingClosurePlatformColumns: ExceptionColumnDef[] = [
  ...platformCommonColumns,
  {
    key: 'detection_type',
    label: 'Detection',
    align: 'center',
    contexts: ['platform'],
    render: (value) =>
      value === 'tail' ? 'Recent (tail)' : 'Gap',
  },
  {
    key: 'missing_sequence',
    label: 'Missing Seq',
    type: 'integer',
    align: 'center',
    contexts: ['platform'],
  },
  {
    key: 'ls_register_close_time',
    label: 'LS Last Close',
    type: 'datetime',
    contexts: ['platform'],
  },
  {
    key: 'last_xero_sync_at',
    label: 'Last Xero Sync',
    type: 'datetime',
    contexts: ['platform'],
  },
  {
    key: 'xero_coverage_anchor',
    label: 'Xero Data Through',
    type: 'datetime',
    contexts: ['platform'],
  },
  {
    key: 'bill_present',
    label: 'Bill in Xero',
    align: 'center',
    contexts: ['platform'],
    render: (value, row) =>
      row.detection_type === 'tail' ? '-' : value ? 'Yes' : 'No',
  },
  {
    key: 'prev_sequence',
    label: 'Prev Seq',
    type: 'integer',
    align: 'right',
    contexts: ['platform'],
  },
  {
    key: 'prev_date',
    label: 'Prev Date',
    type: 'date',
    contexts: ['platform'],
  },
  {
    key: 'prev_invoice_number',
    label: 'Prev Inv #',
    contexts: ['platform'],
    link: { field: 'prev_xero_link', system: 'xero' },
  },
  {
    key: 'next_sequence',
    label: 'Next Seq',
    type: 'integer',
    align: 'right',
    contexts: ['platform'],
  },
  {
    key: 'next_date',
    label: 'Next Date',
    type: 'date',
    contexts: ['platform'],
  },
  {
    key: 'next_invoice_number',
    label: 'Next Inv #',
    contexts: ['platform'],
    link: { field: 'next_xero_link', system: 'xero' },
  },
]

export const registerClosureMissingInXeroConfig: ExceptionConfig = {
  codes: ['ex-register-closure-missing-in-xero'],
  columns: [buildIssuesColumn(), ...missingClosureInspectorColumns, ...missingClosurePlatformColumns],
  defaultSortColumn: 'next_date',
  defaultSortDirection: 'desc',
  complexity: 'simple',
  rowKeyField: 'object_id',
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      textPart(row.outlet_name),
      typeof row.missing_sequence === 'number'
        ? `seq ${row.missing_sequence} missing`
        : null,
      row.prev_date && row.next_date
        ? `${datePart(row.prev_date)} - ${datePart(row.next_date)}`
        : null,
    ]),
}
